/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import Tour, { TourStep } from '../components/Tour';
import { useI18n } from './i18n';

/**
 * Registro global de tour: a página ativa registra seus steps (useRegisterTour) e o
 * botão global (useTourControls.start) inicia o tour da tela atual. O <Tour> é montado
 * uma única vez aqui, sobre todo o app.
 */
interface TourCtx {
  available: boolean;       // a tela atual tem um tour registrado
  start: () => void;
  register: (steps: TourStep[] | null) => void;
}

const Ctx = createContext<TourCtx | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [steps, setSteps] = useState<TourStep[] | null>(null);
  const [running, setRunning] = useState(false);

  const register = useCallback((s: TourStep[] | null) => {
    setSteps(s);
    if (!s) setRunning(false); // ao sair da tela, encerra qualquer tour aberto
  }, []);
  const start = useCallback(() => { setRunning(true); }, []);

  return (
    <Ctx.Provider value={{ available: !!(steps && steps.length), start, register }}>
      {children}
      {running && steps && steps.length > 0 && (
        <Tour
          steps={steps}
          onClose={() => setRunning(false)}
          labels={{ skip: t('tour_skip'), prev: t('tour_prev'), next: t('tour_next'), done: t('tour_done') }}
        />
      )}
    </Ctx.Provider>
  );
}

export function useTourControls(): TourCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useTourControls deve estar dentro de <TourProvider>');
  return c;
}

/** A página chama isto com seus steps (memoize o array p/ evitar re-registro a cada render). */
export function useRegisterTour(steps: TourStep[]) {
  const { register } = useTourControls();
  useEffect(() => {
    register(steps);
    return () => register(null);
  }, [register, steps]);
}
